import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { memo } from 'react';
import { defaultCustomParams } from '../../../potree/sketchObjects/custom/evaluateCustomGlyph';
import { DIMENSION_OFFSET_PARAM, dimensionOffsetOf } from '../../../potree/sketchObjects/custom/customSketchObjectType';
import { T } from '../../../translation/src';
import { setTestValue } from '../canvas/testInstance';
import { MIN_TEST_WALL_RADIUS, testWallThickness } from '../canvas/testOpening';
import { testSize, useEditorState, useObjectEditor } from '../EditorContext';
import { FieldRow, NumberField, PanelButton, PanelSection, SelectField, ToggleRow } from '../panels/fields';
import { ModelSettings } from './ModelSettings';
import { ObjectDetails } from './ObjectDetails';

/** Tighter than the smallest radius reads as a mistake: snap to it (keeping the direction). */
const clampRadius = (radius: number): number => (radius === 0 ? 0 : Math.sign(radius) * Math.max(MIN_TEST_WALL_RADIUS, Math.abs(radius)));

/**
 * Test mode: a stand-in for a placed object. Drag the green handles on the
 * canvas or type a size, and set the parameters like a user would in the
 * properties panel, to check that repeats, pinned parts and optional parts
 * behave. Everything applies live — while typing and while stepping
 * the arrows, since Test mode keeps no history.
 */
const TestPanel_: React.FC = () => {
  const { definition, ui, setUi } = useObjectEditor();
  const { spec } = useEditorState();
  const opening = spec.opening ?? null;
  /** Dimensions: only their length (the distance between the ends) changes in a project. */
  const dimension = !!spec.dimension;
  const params = { ...defaultCustomParams(spec), ...ui.test.params };
  const size = testSize(ui.test, spec);
  const setTest = (patch: Partial<typeof ui.test>) => setUi((u) => ({ ...u, test: { ...u.test, ...patch } }));
  // Like the properties panel of a placed object: the value, then the object's linked values follow.
  const setValue = (key: string, value: number | string | boolean) => setUi((u) => ({ ...u, test: setTestValue(spec, u.test, key, value) }));
  const setParam = setValue;
  // A dimension's offset is not a param of its own: kept with the test params, read as the `offset` variable.
  const setTestOffset = (offset: number) => setUi((u) => ({ ...u, test: { ...u.test, params: { ...u.test.params, [DIMENSION_OFFSET_PARAM]: offset } } }));

  // A smart object's model being placed: the edit tab's model settings instead of the test placement.
  if (ui.editModel && spec.model3d) return <ModelSettings />;

  return (
    <>
      {/* A smart object has no Edit mode: its name and category are edited here. */}
      {definition.smart && <ObjectDetails />}
      <PanelSection
        title={<T _str="test placement" />}
        actions={
          <PanelButton onClick={() => setUi((u) => ({ ...u, test: { params: {} } }))}>
            <ArrowPathIcon className="h-3.5 w-3.5" />
            <T _str="reset" swc />
          </PanelButton>
        }
      >
        {opening ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="the drawing in an opening of a test wall: drag the jambs or type a width, bend the wall and flip the opening to see how it behaves in a project" swc />
          </p>
        ) : dimension ? (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="drag the ends or type a length. A placed dimension always shows the length it measures." swc />
          </p>
        ) : (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            <T _str="the same handles and gizmo as a placed object: drag them, or type a size, to see how it behaves in a project" swc />
          </p>
        )}
        <FieldRow label={dimension ? <T _str="length" swc /> : <T _str="width" swc />}>
          <NumberField value={size.width} unit="m" min={0.05} max={100} onCommit={(width) => setValue('width', width)} onLiveChange={(width) => setValue('width', width)} />
        </FieldRow>
        {dimension && (
          <FieldRow label={<T _str="offset" swc />} hint={<T _str="how far the line sits off the measured points (the third click when placing)" swc />}>
            <NumberField value={dimensionOffsetOf(ui.test.params)} unit="m" min={-100} max={100} onCommit={(offset) => setTestOffset(offset)} onLiveChange={(offset) => setTestOffset(offset)} />
          </FieldRow>
        )}
        {!opening && !dimension && (
          <FieldRow label={<T _str="depth" swc />}>
            <NumberField value={size.depth} unit="m" min={0.05} max={100} onCommit={(depth) => setValue('depth', depth)} onLiveChange={(depth) => setValue('depth', depth)} />
          </FieldRow>
        )}
        {!dimension && (
          <FieldRow label={<T _str="height" swc />}>
            <NumberField value={size.height} unit="m" min={0.05} max={100} onCommit={(height) => setTest({ height })} onLiveChange={(height) => setTest({ height })} />
          </FieldRow>
        )}
      </PanelSection>

      {opening && (
        <PanelSection title={<T _str="test wall" />}>
          <FieldRow label={<T _str="wall thickness" swc />}>
            <NumberField value={testWallThickness(ui.test)} unit="m" min={0.02} max={2} onCommit={(wall) => setTest({ wall })} onLiveChange={(wall) => setTest({ wall })} />
          </FieldRow>
          <FieldRow label={<T _str="wall radius" swc />} hint={<T _str="0 = a straight wall; a negative radius bends the other way (or drag the blue handle)" swc />}>
            <NumberField
              value={ui.test.wallRadius ?? 0}
              unit="m"
              min={-100}
              max={100}
              onCommit={(radius) => setTest({ wallRadius: clampRadius(radius) })}
              onLiveChange={(radius) => setTest({ wallRadius: clampRadius(radius) })}
            />
          </FieldRow>
          {opening.kind === 'DOOR' && <ToggleRow label={<T _str="hinge at the other jamb" swc />} checked={!!ui.test.hingeAtEnd} onChange={(hingeAtEnd) => setTest({ hingeAtEnd })} />}
          <ToggleRow label={<T _str="flip to the other side" swc />} checked={!!ui.test.otherSide} onChange={(otherSide) => setTest({ otherSide })} />
        </PanelSection>
      )}

      {spec.params.length > 0 && (
        <PanelSection title={<T _str="parameters" />}>
          {spec.params.map((p) => {
            const value = params[p.key];
            switch (p.kind) {
              case 'float':
              case 'int': {
                const current = typeof value === 'number' ? value : p.default;
                const integer = p.kind === 'int';
                const unit = p.kind === 'float' && p.distance ? 'm' : undefined;
                return (
                  <FieldRow key={p.key} label={p.label}>
                    <NumberField value={current} integer={integer} unit={unit} min={p.min} max={p.max} onCommit={(v) => setParam(p.key, v)} onLiveChange={(v) => setParam(p.key, v)} />
                  </FieldRow>
                );
              }
              case 'boolean':
                return <ToggleRow key={p.key} label={p.label} checked={value === true} onChange={(v) => setParam(p.key, v)} />;
              case 'select':
                return (
                  <FieldRow key={p.key} label={p.label}>
                    <SelectField value={typeof value === 'string' ? value : p.default} options={p.options} onChange={(v) => setParam(p.key, v)} />
                  </FieldRow>
                );
              default:
                return null;
            }
          })}
        </PanelSection>
      )}
    </>
  );
};

export const TestPanel = memo(TestPanel_);
